/*
 * Starter prompts and composer placeholders in the chosen answer language.
 *
 * FIXED STRINGS, written into the build. Nothing here calls a model at run
 * time. They are shown only when a language other than Auto is chosen, so
 * the choice is visible before the first question. Auto and English use the
 * English originals.
 *
 * Only these six strings change language. The heading, subtitle, disclaimer,
 * terms links and every legal text stay English: legal wording needs reviewed
 * translations, not ours.
 *
 * NOT REVIEWED BY NATIVE SPEAKERS. These were written for this build and
 * should be checked by a native speaker per language before being relied on.
 * Rules they follow, pinned by starterStrings.test.js:
 *   - the same four questions, in the same order, with every number, unit
 *     and "RAS" kept, because the prompts were chosen against what the manual
 *     corpus can answer (see the comment on `examples` in App.js);
 *   - the first stays an IMPROVEMENT question ("improve oxygen levels"),
 *     never a crisis: a crisis wording is classified as an emergency, which
 *     correctly suppresses the sourcing surface, so the prompt would lead
 *     nowhere;
 *   - Portuguese follows Brazilian usage; Chinese is Simplified.
 */

const EN = {
  placeholderHero: "Hi, I'm Gia, AquaNetica's AI assistant. Ask me about pumps, biofilters, water chemistry…",
  placeholderFollowUp: 'Ask a follow-up…',
  examples: [
    'How can I improve oxygen levels in my RAS?',
    'What size pump do I need for a 40 m³ RAS at 6 m head?',
    'What UV dose do I need to disinfect 30 m³/hr of intake water?',
    "I'm adding a 20 m³ grow-out tank. What equipment will it need?",
  ],
};

export const STARTER_STRINGS = Object.freeze({
  auto: EN,
  en: EN,
  es: {
    placeholderHero: 'Hola, soy Gia. Pregúntame sobre bombas, biofiltros, química del agua…',
    placeholderFollowUp: 'Haz otra pregunta…',
    examples: [
      '¿Cómo puedo mejorar los niveles de oxígeno en mi RAS?',
      '¿Qué tamaño de bomba necesito para un RAS de 40 m³ con 6 m de altura?',
      '¿Qué dosis de UV necesito para desinfectar 30 m³/h de agua de entrada?',
      'Voy a añadir un tanque de engorde de 20 m³. ¿Qué equipos necesitará?',
    ],
  },
  pt: {
    placeholderHero: 'Olá, eu sou a Gia. Pergunte sobre bombas, biofiltros, química da água…',
    placeholderFollowUp: 'Faça outra pergunta…',
    examples: [
      'Como posso melhorar os níveis de oxigênio no meu RAS?',
      'De que tamanho de bomba preciso para um RAS de 40 m³ com 6 m de altura manométrica?',
      'Que dose de UV preciso para desinfetar 30 m³/h de água de captação?',
      'Vou adicionar um tanque de engorda de 20 m³. De quais equipamentos ele vai precisar?',
    ],
  },
  vi: {
    placeholderHero: 'Xin chào, tôi là Gia. Hãy hỏi tôi về máy bơm, bộ lọc sinh học, hóa học nước…',
    placeholderFollowUp: 'Đặt câu hỏi tiếp theo…',
    examples: [
      'Làm thế nào để cải thiện nồng độ oxy trong hệ thống RAS của tôi?',
      'Tôi cần máy bơm cỡ nào cho hệ thống RAS 40 m³ với cột áp 6 m?',
      'Tôi cần liều UV bao nhiêu để khử trùng 30 m³/giờ nước cấp?',
      'Tôi sắp thêm một bể nuôi thương phẩm 20 m³. Bể này cần những thiết bị gì?',
    ],
  },
  id: {
    placeholderHero: 'Halo, saya Gia. Tanyakan tentang pompa, biofilter, kimia air…',
    placeholderFollowUp: 'Ajukan pertanyaan lanjutan…',
    examples: [
      'Bagaimana cara meningkatkan kadar oksigen di RAS saya?',
      'Pompa ukuran berapa yang saya perlukan untuk RAS 40 m³ dengan head 6 m?',
      'Berapa dosis UV yang diperlukan untuk mendisinfeksi 30 m³/jam air masuk?',
      'Saya akan menambah bak pembesaran 20 m³. Peralatan apa saja yang dibutuhkan?',
    ],
  },
  zh: {
    placeholderHero: '你好，我是 Gia。可以问我水泵、生物滤器、水化学等问题…',
    placeholderFollowUp: '继续提问…',
    examples: [
      '如何提高我的 RAS 系统中的溶氧水平？',
      '40 m³ 的 RAS 系统、6 m 扬程，需要多大的水泵？',
      '对 30 m³/小时的进水进行消毒，需要多大的 UV 剂量？',
      '我要新增一个 20 m³ 的养成池，需要配备哪些设备？',
    ],
  },
  th: {
    placeholderHero: 'สวัสดี ฉันชื่อ Gia ถามฉันเรื่องปั๊ม ไบโอฟิลเตอร์ เคมีของน้ำ…',
    placeholderFollowUp: 'ถามคำถามต่อ…',
    examples: [
      'ฉันจะเพิ่มระดับออกซิเจนในระบบ RAS ได้อย่างไร',
      'ระบบ RAS ขนาด 40 m³ ที่ความสูงส่ง 6 m ต้องใช้ปั๊มขนาดเท่าไร',
      'ต้องใช้ปริมาณ UV เท่าไรเพื่อฆ่าเชื้อน้ำเข้า 30 m³/ชม.',
      'ฉันกำลังจะเพิ่มบ่อเลี้ยงขุนขนาด 20 m³ ต้องใช้อุปกรณ์อะไรบ้าง',
    ],
  },
  tr: {
    placeholderHero: 'Merhaba, ben Gia. Bana pompalar, biyofiltreler, su kimyası hakkında soru sorun…',
    placeholderFollowUp: 'Başka bir soru sorun…',
    examples: [
      'RAS sistemimde oksijen seviyelerini nasıl artırabilirim?',
      "6 m basma yüksekliğinde 40 m³'lük bir RAS için ne büyüklükte bir pompa gerekir?",
      '30 m³/saat giriş suyunu dezenfekte etmek için ne kadar UV dozu gerekir?',
      "20 m³'lük bir büyütme tankı ekliyorum. Hangi ekipmanlara ihtiyacı olacak?",
    ],
  },
});

/** The strings for a code; anything unknown is the English original. */
export function starterStringsFor(code) {
  return STARTER_STRINGS[code] || EN;
}

/** The lang attribute for translated text; none for Auto or English originals. */
export function starterLangAttr(code) {
  return code && code !== 'auto' && STARTER_STRINGS[code] ? code : undefined;
}
